/**
 * Mobile navigation config — bottom tab bar + "More" drawer + title resolver.
 *
 * Routes mirror the desktop Sidebar so deep links stay identical; only the
 * presentation differs. Isolated in src/mobile/** for clean rollback.
 */
import {
  Home,
  CheckSquare,
  Target,
  Menu,
  LayoutGrid,
  type LucideIcon,
  PlusCircle,
  Library,
  TreeDeciduous,
  ClipboardList,
  MessageSquare,
  Inbox,
  History,
  Settings,
  Search,
  BarChart3,
  Wrench,
  PlayCircle,
  Flag,
  Calendar,
  AlertTriangle,
  Layers,
  Map as MapIcon,
  Activity,
  FileText,
  Clock,
  ArrowLeftRight,
  ListChecks,
} from 'lucide-react';
import type { ElementType } from 'react';
import { parseItemDetailPath } from './mobileItemRoutes';
import { ROLE_MENU_SETTINGS_HREF, type RoleMenu, type RoleMenuIcon } from '../lib/contributorNavProfiles';
import { AREA_LABEL, type AppArea } from '../lib/appAreas';
import type { NavSection } from '../components/prioritization/useSidebarNavigation';

export interface MobileNavItem {
  label: string;
  href: string;
  /** A lucide icon, or a desktop sidebar icon (the More pages list draws those). */
  icon: LucideIcon | ElementType;
  /** When set, tapping this tab opens a submenu sheet instead of navigating to href. */
  submenu?: MobileNavItem[];
}

/**
 * Tab ids used only for active-state matching (not routes).
 *
 * The bottom bar mirrors the desktop Sidebar's operating-domain vocabulary
 * (Item Management, Cycles) rather than mobile-only nouns, so a user who learns
 * one surface can navigate the other. `Items` and `Cycles` open submenu sheets;
 * the remaining domains (Strategy, Decisions & plans, Analyze) live in the
 * "More" drawer, also keyed to the desktop domain names.
 */
export const ITEMS_TAB_ID = '__tab_items__';
export const CYCLES_TAB_ID = '__tab_cycles__';

/**
 * Five thumb-reach destinations pinned to the bottom bar.
 * `__search__` and `__more__` are sentinels handled as in-shell overlays
 * (not URLs), so the desktop router never needs a new route.
 */
export const SEARCH_SENTINEL = '__search__';
export const MORE_SENTINEL = '__more__';

export const TAB_ITEMS: MobileNavItem[] = [
  { label: 'Home', href: '/prioritization/dashboard', icon: Home },
  {
    // Item Management domain (desktop: Intake + Items). Capture, shape, score.
    label: 'Items',
    href: ITEMS_TAB_ID,
    icon: Library,
    submenu: [
      { label: 'All Items', href: '/prioritization/items', icon: Library },
      { label: 'Submit an idea', href: '/prioritization/intake/submit', icon: PlusCircle },
      { label: 'Triage Queue', href: '/prioritization/intake/triage', icon: AlertTriangle },
      { label: 'Values', href: '/prioritization/scoring', icon: BarChart3 },
    ],
  },
  {
    // Cycles & Execution domain. The core act — run cycles, hold sessions, and
    // the outputs those produce (priority lists, the exchange).
    label: 'Cycles',
    href: CYCLES_TAB_ID,
    icon: PlayCircle,
    submenu: [
      { label: 'Cycles', href: '/prioritization/cycles', icon: Calendar },
      { label: 'Tracks', href: '/prioritization/tracks', icon: Layers },
      { label: 'Sessions', href: '/prioritization/sessions', icon: Target },
      { label: 'Item Pools', href: '/prioritization/lists', icon: ListChecks },
      // Exchange hub first (cycles / in-your-court); executive inbox is a peer surface.
      { label: 'Cascade', href: '/prioritization/cascade', icon: MessageSquare },
      { label: 'Executive Inbox', href: '/prioritization/cascade/executive-inbox', icon: Inbox },
    ],
  },
  { label: 'My work', href: '/prioritization/my-work', icon: CheckSquare },
  { label: 'More', href: MORE_SENTINEL, icon: Menu },
];

/** The in-shell apps directory — the address the desktop sidebar's Apps link opens. */
export const APPS_HREF = '/prioritization/apps';

/** Everything else, surfaced through the slide-up drawer. */
export interface MobileNavSection {
  label: string;
  items: MobileNavItem[];
}

/**
 * Drawer sections. One section per remaining desktop operating-domain, using
 * the same names the desktop Sidebar uses, so the mobile drawer reads as a
 * compressed version of the same map — not a separate taxonomy. These are the
 * quick picks; every other desktop sidebar page is listed below them under
 * "More pages" (phoneMorePages), drawn from the desktop menu itself.
 */
export const DRAWER_SECTIONS: MobileNavSection[] = [
  {
    label: 'Strategy',
    items: [
      { label: 'Strategies', href: '/prioritization/strategy', icon: TreeDeciduous },
      { label: 'Objectives', href: '/prioritization/strategy/objectives', icon: Target },
      { label: 'Goals', href: '/prioritization/strategy/goals', icon: Flag },
      { label: 'OKRs', href: '/prioritization/strategy/okrs', icon: BarChart3 },
    ],
  },
  {
    label: 'Decisions & plans',
    items: [
      { label: 'Plans', href: '/planning/plans', icon: ClipboardList },
      { label: 'Decisions', href: '/prioritization/decisions', icon: History },
    ],
  },
  {
    label: 'Analyze',
    items: [
      { label: 'Tools', href: '/prioritization/tools', icon: Wrench },
      { label: 'Reports', href: '/prioritization/reports', icon: BarChart3 },
    ],
  },
  {
    label: 'Workspace',
    items: [
      // The App Layer. Filtered out by MobileDrawer when the user has no
      // entitled apps, matching the desktop sidebar rather than dead-ending.
      // The same address the desktop sidebar's Apps link opens.
      { label: 'Apps', href: APPS_HREF, icon: LayoutGrid },
      { label: 'Search', href: SEARCH_SENTINEL, icon: Search },
      { label: 'Settings', href: '/prioritization/settings', icon: Settings },
    ],
  },
];

const TOOL_TITLES: Record<string, string> = {
  'wsjf': 'WSJF',
  'cost-of-delay': 'Cost of Delay',
  'rice-scoring': 'RICE Scoring',
  'moscow-method': 'MoSCoW Method',
  'impact-effort-matrix': 'Impact / Effort Matrix',
  'custom-matrix': 'Custom 2×2 Matrix',
  'simple-weighted-scoring': 'Weighted Scoring',
  'pairwise-ranking': 'Pairwise Comparison',
  'fast-vs-right': 'Fast vs Right Framework',
  'prune-product-tree': 'Prune the Tree',
};

/** Best-effort human title for the current path (used in the mobile header). */
export function resolveTitle(pathname: string): string {
  // Item detail: /prioritization/items/<uuid> — show generic "Item" rather than
  // the raw ID. Named sibling routes (/items/triage) fall through to the
  // prettified-segment fallback below.
  if (parseItemDetailPath(pathname)) return 'Item';

  // Cycle detail: /prioritization/cycles/<uuid> — show "Cycle" rather than the raw ID.
  if (/^\/prioritization\/cycles\/[^/]+$/.test(pathname)) return 'Cycle';

  // Session list and detail.
  if (pathname === '/prioritization/sessions') return 'Sessions';
  if (/^\/prioritization\/sessions\/[^/]+\/results$/.test(pathname)) return 'Results';
  if (/^\/prioritization\/sessions\/[^/]+$/.test(pathname)) return 'Session';
  // A roadmap or tournament opens by its id; the id is not a title.
  if (/^\/prioritization\/(roadmap|product-roadmap)\/[^/]+$/.test(pathname)) return 'Roadmap';
  if (pathname === '/prioritization/tournaments/new') return 'New tournament';
  if (/^\/prioritization\/tournaments\/[^/]+$/.test(pathname)) return 'Tournament';
  if (pathname === '/prioritization/tracks') return 'Tracks';
  if (/^\/prioritization\/tracks\/[^/]+$/.test(pathname)) return 'Track';
  if (/^\/prioritization\/strategy\/list$/.test(pathname)) return 'Strategies';
  if (/^\/prioritization\/commitments\/[^/]+$/.test(pathname)) return 'Commitment';
  if (/^\/prioritization\/tools\//.test(pathname)) {
    const slug = pathname.split('/').pop() ?? 'Tool';
    // The names the Tools page gives them (pages/prioritization/Tools.tsx);
    // capitalising the address turns WSJF into "Wsjf" and MoSCoW into "Moscow Method".
    return TOOL_TITLES[slug] ?? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  const all: MobileNavItem[] = [
    ...TAB_ITEMS.filter((t) => !t.href.startsWith('__')),
    // Include submenu children (Sessions, Item Pools, the Cascade, …) so
    // their header titles resolve from the label rather than a prettified slug.
    ...TAB_ITEMS.flatMap((t) => t.submenu ?? []).filter((i) => !i.href.startsWith('__')),
    ...DRAWER_SECTIONS.flatMap((s) => s.items).filter((i) => !i.href.startsWith('__')),
  ];
  // Longest matching href wins so /strategy/okrs beats /strategy.
  const match = all
    .filter((i) => pathname === i.href || pathname.startsWith(i.href + '/') || pathname.startsWith(i.href + '?'))
    .sort((a, b) => b.href.length - a.href.length)[0];
  if (match) return match.label;

  // Fallback: prettify the last path segment.
  const seg = pathname.split('?')[0].split('/').filter(Boolean).pop() ?? 'Priorities';
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Top-level tab destinations — no fixed title bar (pages own their headings). */
export const PRIORITIZATION_TOP_LEVEL_PATHS = new Set([
  '/prioritization',
  '/prioritization/dashboard',
  '/prioritization/my-work',
  '/prioritization/my-tasks',
  '/prioritization/cycles',
  '/prioritization/search',
]);

export function isPrioritizationTopLevelPath(pathname: string): boolean {
  return PRIORITIZATION_TOP_LEVEL_PATHS.has(pathname);
}

/** Which bottom tab should appear active for a given path. */
export function activeTabHref(pathname: string): string | null {
  for (const tab of TAB_ITEMS) {
    if (tab.submenu?.length) {
      const onChild = tab.submenu.some(
        (child) =>
          pathname === child.href ||
          pathname.startsWith(child.href + '/') ||
          pathname.startsWith(child.href + '?'),
      );
      if (onChild) return tab.href;
    }
  }

  const candidates = TAB_ITEMS.filter((t) => !t.href.startsWith('__'));
  const match = candidates
    .filter((t) => pathname === t.href || pathname.startsWith(t.href + '/') || pathname.startsWith(t.href + '?'))
    .sort((a, b) => b.href.length - a.href.length)[0];
  return match?.href ?? null;
}

// ─── Role-based menu on the phone (roadmap R-7) ──────────────────────────────

const ROLE_MENU_ICONS: Record<RoleMenuIcon, LucideIcon> = {
  sessions: Target,
  tasks: CheckSquare,
  exchange: ArrowLeftRight,
  results: Activity,
  reports: FileText,
  plan: MapIcon,
  items: Library,
  submit: PlusCircle,
  cycles: Clock,
  apps: LayoutGrid,
};

const ROLE_MENU_TASKS_HREF = '/prioritization/my-work';

/**
 * The phone drawer for a person on the role-based menu. It is drawn from the
 * same `RoleMenu` the desktop short side menu draws (`buildRoleMenu`), so the
 * phone and the desktop list the same links. Search and Settings are added at
 * the end, as the desktop short menu carries Settings at its foot.
 */
export function roleMenuDrawerSections(menu: RoleMenu): MobileNavSection[] {
  const sections: MobileNavSection[] = menu.sections.map((section) => ({
    label: section.label,
    items: section.links
      // My Tasks sits on the tab bar (roleMenuTabItems), not in the drawer.
      .filter((link) => link.href !== ROLE_MENU_TASKS_HREF)
      .map((link) => ({ label: link.label, href: link.href, icon: ROLE_MENU_ICONS[link.icon] })),
  }));
  sections.push({
    label: 'Settings',
    items: [
      { label: 'Search', href: SEARCH_SENTINEL, icon: Search },
      { label: 'Settings', href: ROLE_MENU_SETTINGS_HREF, icon: Settings },
    ],
  });
  return sections.filter((section) => section.items.length > 0);
}

/**
 * The bottom bar for a person on the role-based menu: Home, My Tasks when the
 * menu has it, and More (the drawer). The full bar's Items and Cycles tabs
 * would offer every person Cycles, Tracks and the Triage Queue whatever their
 * roles, so they are left out here.
 */
export function roleMenuTabItems(menu: RoleMenu): MobileNavItem[] {
  const hasTasks = menu.sections.some((s) => s.links.some((l) => l.href === ROLE_MENU_TASKS_HREF));
  return [
    { label: 'Home', href: '/prioritization/dashboard', icon: Home },
    ...(hasTasks ? [{ label: 'Tasks', href: ROLE_MENU_TASKS_HREF, icon: CheckSquare }] : []),
    { label: 'More', href: MORE_SENTINEL, icon: Menu },
  ];
}

// ─── Every desktop sidebar page on the phone (audit 2026-11-02, #33) ─────────

/** Groups drawn last, in this order, as the desktop sidebar draws them last. */
const MORE_PAGES_TAIL = ['Reports', 'Decisions', 'Help'];

/** Address without its query: `/planning/plans?catalog=1` is the Plans page. */
function pathOf(href: string): string {
  return href.split('?')[0];
}

/** Every concrete address the bottom bar, its submenus and the drawer's quick picks reach. */
export function phoneQuickPickHrefs(
  tabs: MobileNavItem[] = TAB_ITEMS,
  drawer: MobileNavSection[] = DRAWER_SECTIONS,
): string[] {
  return [...tabs.flatMap((t) => (t.submenu ? t.submenu : [t])), ...drawer.flatMap((s) => s.items)]
    .map((i) => i.href)
    .filter((href) => !href.startsWith('__'));
}

/**
 * The phone's "More pages": every page the desktop sidebar offers, in every
 * area, that the bottom bar and the drawer's quick picks do not already reach.
 *
 * `areaMenus` is the desktop sidebar's menu for each area
 * (composeAreaNavigation over useSidebarNavigation), so the gates — modules,
 * flags, space type, permissions — are the desktop's, not a second copy.
 * Settings pages are left out: the Settings quick pick opens their index.
 */
export function phoneMorePages(
  areaMenus: Array<{ area: AppArea; sections: NavSection[] }>,
  alreadyOnPhone: Iterable<string>,
): MobileNavSection[] {
  const reached = new Set(Array.from(alreadyOnPhone, pathOf));
  const seen = new Set<string>();
  const groups = new Map<string, MobileNavItem[]>();

  const add = (label: string, item: { name: string; href: string; icon: ElementType }) => {
    if (reached.has(pathOf(item.href)) || seen.has(item.href)) return;
    seen.add(item.href);
    const list = groups.get(label) ?? [];
    list.push({ label: item.name, href: item.href, icon: item.icon });
    groups.set(label, list);
  };

  const labelled = areaMenus.flatMap(({ area, sections }) =>
    sections
      .filter((section) => section.sectionId !== 'settings')
      .map((section) => {
        if (!section.items) return { section, label: section.groupLabel ?? 'Home' };
        // The Plans and Action menus name their sections for the area they sit in
        // ("Capabilities", "The plan in force"); on one list they say which area.
        const areaOwnMenu =
          (area === 'plans' || area === 'action') && section.flatSection === true && section.sectionId === 'monitor';
        return { section, label: areaOwnMenu ? `${AREA_LABEL[area]}: ${section.name}` : section.groupLabel ?? section.name };
      }),
  );
  const shared = (label: string) => label === 'Home' || MORE_PAGES_TAIL.includes(label);

  // Each page is listed once, so where it goes first is where it stays. The
  // areas' own sections go first: the shared Reports list places "Session
  // Reports" at /prioritization/reports/sessions, the same address as the
  // Cycles section's "Session Results", and must not take it from Cycles.
  for (const pass of [false, true]) {
    for (const { section, label } of labelled) {
      if (shared(label) !== pass) continue;
      if (!section.items) {
        if (section.href) add(label, { name: section.name, href: section.href, icon: section.icon });
        continue;
      }
      for (const item of section.items) {
        if (item.isDivider) continue;
        add(label, item);
        for (const sub of item.subItems ?? []) add(label, sub);
      }
    }
  }

  // Home's shared pages first, Reports, Decisions and Help last, the areas between.
  const rank = (label: string) => {
    if (label === 'Home') return 0;
    const tail = MORE_PAGES_TAIL.indexOf(label);
    return tail === -1 ? 1 : 2 + tail;
  };
  return Array.from(groups.entries())
    .map(([label, items], index) => ({ label, items, index }))
    .sort((a, b) => rank(a.label) - rank(b.label) || a.index - b.index)
    .map(({ label, items }) => ({ label, items }));
}
